import { FunctionComponent, useState, useCallback, useRef, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { Dropdown } from "grabcad-ui-elements";
import { DropdownProps } from "semantic-ui-react";
import {
    SEARCH_COMPANIES_FOR_DROPDOWN,
    SearchCompaniesForDropdownResult,
    SearchCompaniesForDropdownVariables,
} from "../../graphql/Queries/Companies/ListCompanies";

const COMPANY_SEARCH_PAGE_SIZE = 20;
const COMPANY_SEARCH_DEBOUNCE_MS = 300;
const COMPANY_SEARCH_MIN_CHARS = 2;

export interface CompanySearchDropdownProps {
    currentCompanyId: number;
    currentCompanyName: string;
    currentAccountNumber?: string | null;
    onChange: (selected: { id: number; name: string }) => void;
}

export const CompanySearchDropdown: FunctionComponent<CompanySearchDropdownProps> = ({
    currentCompanyId,
    currentCompanyName,
    currentAccountNumber,
    onChange,
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    const [searchCompanies, { data, loading }] = useLazyQuery<
        SearchCompaniesForDropdownResult,
        SearchCompaniesForDropdownVariables
    >(SEARCH_COMPANIES_FOR_DROPDOWN);

    const handleSearchChange = useCallback(
        (_e: React.SyntheticEvent, { searchQuery: q }: { searchQuery: string }) => {
            setSearchQuery(q);

            const trimmed = q.trim();

            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
            debounceTimer.current = setTimeout(() => {
                if (trimmed.length >= COMPANY_SEARCH_MIN_CHARS) {
                    void searchCompanies({
                        variables: {
                            search: trimmed,
                            take: COMPANY_SEARCH_PAGE_SIZE,
                            skip: 0,
                        },
                    });
                }
            }, COMPANY_SEARCH_DEBOUNCE_MS);
        },
        [searchCompanies]
    );

    const searchedCompanies = data?.listCompanies?.companies ?? [];

    const currentCompanyOption = {
        key: currentCompanyId,
        value: currentCompanyId,
        text: currentAccountNumber
            ? `${currentCompanyName} (${currentAccountNumber})`
            : currentCompanyName,
    };

    // Build options from search results only; current company is always visible via the `text` prop
    let options: { key: number; value: number; text: string }[] = [];
    if (searchQuery.trim().length >= COMPANY_SEARCH_MIN_CHARS && searchedCompanies.length > 0) {
        options = searchedCompanies
            .filter((c) => c.customerInfo?.accountNumber)
            .map((c) => ({
                key: c.id,
                value: c.id,
                text: `${c.name} (${c.customerInfo!.accountNumber})`,
            }));
    }

    return (
        <Dropdown
            fluid
            selection
            search
            data-testid="company-search-dropdown"
            options={options}
            value={currentCompanyId}
            text={currentCompanyOption.text}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            loading={loading}
            noResultsMessage={
                searchQuery.trim().length >= COMPANY_SEARCH_MIN_CHARS
                    ? "No companies found"
                    : "Type to search companies"
            }
            onChange={(_e: React.SyntheticEvent<HTMLElement>, dropdownData: DropdownProps) => {
                const selectedId = Number(dropdownData.value);
                const selectedCompany = searchedCompanies.find((c) => c.id === selectedId);
                if (!selectedCompany) {
                    return;
                }
                setSearchQuery("");
                onChange({ id: selectedId, name: selectedCompany.name });
            }}
        />
    );
};
